import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import IconeAjuda from '@/global/components/IconeAjuda'
import { MarkdownEditor } from '@/global/components/Markdown/MarkdownEditor'
import { SelectClientes } from '@/global/components/Selects'
import Grid from '@/global/components/ui/Grid'
import { CategoriaEnum } from '@acme/auth'
import {
    CardRadioForm,
    FormDatePicker,
    FormField,
    FormFieldAutocomplete,
    FormFieldCheckbox,
    FormFieldSelect,
} from 'global/components/Form'
import {
    BellRingIcon,
    MessageSquareTextIcon,
    PictureInPictureIcon,
} from 'lucide-react'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { CampanhaImagemEnvio, CreateCampanha } from 'service/api'

type OpcaoCategoria = { value: number; label: string }

/** `Publico` (0) só existe para permissões — não é alvo de entrega. */
const OPCOES_DE_CATEGORIA: OpcaoCategoria[] = Object.entries(CategoriaEnum)
    .filter(([chave]) => Number(chave) > 0)
    .map(([chave, nome]) => ({ value: Number(chave), label: String(nome) }))

interface FormCampanhaProps {
    imagens: CampanhaImagemEnvio[]
    anexarImagens: (arquivos: File[]) => Promise<string[]>
}

export function FormCampanha({ imagens, anexarImagens }: FormCampanhaProps) {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<CreateCampanha>()

    const modos = watch('modos')
    const corpoMarkdown = watch('corpoMarkdown')
    const isUmaVez = watch('umaVez')

    // A imagem ainda não foi enviada, então a pré-visualização resolve o token
    // pelo base64 que está em memória.
    const previewImagem = useMemo(() => {
        const urls = new Map(
            imagens.map(imagem => [
                imagem.id,
                `data:${imagem.mime};base64,${imagem.base64}`,
            ]),
        )

        return (imagemId: string) => urls.get(imagemId)
    }, [imagens])

    function alternar<T>(lista: T[], item: T, marcado: boolean) {
        return marcado
            ? [...lista, item]
            : lista.filter(atual => atual !== item)
    }

    const isToastBadge = useMemo(
        () => modos.includes('toast') || modos.includes('badge'),
        [modos],
    )

    return (
        <Grid container>
            <Grid item xs={12}>
                <FormField
                    name="titulo"
                    label="Título da notificação"
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <HorizontalDivider>
                    Modos{' '}
                    <IconeAjuda>
                        <div className="[&_h3]:[&_svg]:size-5.5 [&_h3]:[&_svg]:stroke-3 [&_h3]:[&_svg]:align-text-bottom">
                            <h3>
                                <BellRingIcon /> Modo central
                            </h3>
                            <br />
                            <p>
                                Exibe a notificação dentro da central de
                                notificações, no canto superior direito da tela.
                                <br />O usuário precisar marcar a notificação
                                como lida.
                            </p>
                            <br />
                            <br />
                            <h3>
                                <MessageSquareTextIcon /> Modo toast
                            </h3>
                            <br />
                            <p>
                                Exibe a nitificação em formato toast, no canto
                                inferior direito da tela.
                                <br />A mensagem é entregue e marcada como lida.
                            </p>
                            <br />
                            <br />
                            <h3>
                                <PictureInPictureIcon /> Modo diálogo
                            </h3>
                            <br />
                            <p>
                                Exibe a mensagem em um diálogo, no meio da tela.
                                <br />O usuário precisa fechar o diálogo ou
                                clicar no botão para marcar como lido.
                            </p>
                            <br />
                            <p className="text-xs text-muted-foreground">
                                Vários modos podem ser selecionados ao mesmo
                                tempo e configurados de forma independente.
                            </p>
                        </div>
                    </IconeAjuda>
                </HorizontalDivider>
                <MensagemErro erro={errors.modos?.message} />
            </Grid>
            <Grid item xs={12} sm={12 / 3}>
                <FormFieldCheckbox
                    label={
                        <span className="inline-flex gap-2">
                            Central
                            <BellRingIcon />
                        </span>
                    }
                    name="_modo_badge"
                    onChange={marcado =>
                        setValue('modos', alternar(modos, 'badge', marcado))
                    }
                />
            </Grid>
            <Grid item xs={12} sm={12 / 3}>
                <FormFieldCheckbox
                    label={
                        <span className="inline-flex gap-2">
                            Toast
                            <MessageSquareTextIcon />
                        </span>
                    }
                    name="_modo_toast"
                    onChange={marcado =>
                        setValue('modos', alternar(modos, 'toast', marcado))
                    }
                />
            </Grid>
            <Grid item xs={12} sm={12 / 3}>
                <FormFieldCheckbox
                    label={
                        <span className="inline-flex gap-2">
                            Diálogo
                            <PictureInPictureIcon />
                        </span>
                    }
                    name="_modo_modal"
                    onChange={marcado =>
                        setValue('modos', alternar(modos, 'modal', marcado))
                    }
                />
            </Grid>
            <Grid item xs={12} className="my-4">
                <HorizontalDivider>
                    Configurações de Toast/Central
                </HorizontalDivider>
            </Grid>
            <Grid item xs={12}>
                <FormFieldSelect
                    name="severity"
                    label="Severidade"
                    options={[
                        { value: 'info', label: 'Informação' },
                        { value: 'success', label: 'Sucesso' },
                        { value: 'warning', label: 'Atenção' },
                        { value: 'error', label: 'Erro' },
                    ]}
                    required={isToastBadge}
                    disabled={!isToastBadge}
                />
            </Grid>
            <Grid item xs={12}>
                <FormField
                    name="mensagem"
                    label="Mensagem"
                    required={isToastBadge}
                    disabled={!isToastBadge}
                    multiline
                    rows={2}
                />
            </Grid>
            <Grid item xs={12} className="my-4">
                <HorizontalDivider>Corpo do diálogo</HorizontalDivider>
            </Grid>
            <Grid item xs={12}>
                <MarkdownEditor
                    value={corpoMarkdown}
                    onChange={valor => setValue('corpoMarkdown', valor)}
                    onAnexarImagens={anexarImagens}
                    resolverImagem={previewImagem}
                    placeholder="Suporta markdown"
                    className="h-96"
                    disabled={!modos.includes('modal')}
                />
                <MensagemErro erro={errors.corpoMarkdown?.message} />
            </Grid>
            <Grid item xs={12} className="my-4">
                <HorizontalDivider>
                    Botão do diálogo (opcional)
                </HorizontalDivider>
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormField
                    name="botaoLabel"
                    label="Rótulo do botão"
                    disabled={!modos.includes('modal')}
                    endAdornment={
                        <IconeAjuda>
                            Por padrão, todo diálogo apresenta o rótulo "Salvar"
                            em seu botão primário, esse campo serve para alterar
                            o rótulo do botão primário do diálogo.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormField
                    name="botaoUrl"
                    label="URL do botão"
                    disabled={!modos.includes('modal')}
                    endAdornment={
                        <IconeAjuda>
                            Pode ser uma url dentro do sistema (ex.:
                            /manutencao-ponto/digitacao) ou externa (ex:
                            https://docs.farolsoftware.com.br).
                            <br />
                            URLs internas navegam direto para a página definida,
                            enquanto as URLs externas abrem em uma nova guia.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item xs={12} className="my-4">
                <HorizontalDivider>Configurações de entrega</HorizontalDivider>
            </Grid>
            <Grid item xs={12}>
                <CardRadioForm
                    name="umaVez"
                    options={[
                        {
                            value: true,
                            titulo: 'Enviar agora',
                            descricao:
                                'A notificação é transmitida na hora para quem estiver online, sem janela de exibição e sem reenvio.',
                        },
                        {
                            value: false,
                            titulo: 'Manter até ler',
                            descricao:
                                'A notificação é reentregue durante todo o período, até o usuário marcar como lido.',
                        },
                    ]}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormDatePicker
                    name="inicio"
                    label="Início"
                    type="datetime-local"
                    required={!isUmaVez}
                    disabled={isUmaVez}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormDatePicker
                    name="fim"
                    label="Fim"
                    type="datetime-local"
                    required={!isUmaVez}
                    disabled={isUmaVez}
                />
            </Grid>
            <Grid item xs={12}>
                <FormFieldAutocomplete
                    name="categorias"
                    label="Entregar para as categorias"
                    options={OPCOES_DE_CATEGORIA}
                    getOptionLabel={opcao => opcao.label}
                    paraCampo={(categorias: number[] = []) =>
                        OPCOES_DE_CATEGORIA.filter(opcao =>
                            categorias.includes(opcao.value),
                        )
                    }
                    paraFormulario={(opcoes: OpcaoCategoria[]) =>
                        opcoes.map(opcao => opcao.value)
                    }
                    multiple
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <SelectClientes
                    name="clienteIds"
                    multiple
                    label="Entregar apenas para os clientes"
                    placeholder="Todos"
                />
            </Grid>
        </Grid>
    )
}

function MensagemErro({ erro }: { erro?: string }) {
    return (
        <p className="mt-1 text-xs text-error">{erro ? erro : <>&nbsp;</>}</p>
    )
}
